import {IDateSelectionStrategy} from "./strategy/date-selection-strategy.interface";
import {IReactionDisposer, makeObservable, observable, reaction, runInAction} from "mobx";
import {DepartureDateSelectionStrategy} from "./strategy/departure-date-selection.strategy";
import {IServiceFactory} from "../../service-factory.interface";
import {ReturnDateSelectionStrategy} from "./strategy/return-date-selection.strategy";
import {MonthModel} from "../../time/month.model";
import {
    IFlightSearchControllerViewModel
} from "../flight-search-controller/flight-search-controller-view-model.interface";
import {
    ILowFareResult,
    IRoundTripLowFareReader,
    LowFareStatusEnum
} from "../../low-fare/low-fare-readers/low-fare-reader.interface";
import {NullableDate} from "../../../types/nullable-types";
import {
    FlightScheduleAvailabilityStatusEnum,
    IFlightDateScheduleAvailability
} from "../../flights-schedule/flights-schedule.service.interface";

export class FlightsDatesSelectorModel {
    constructor(public readonly flightSearchController: IFlightSearchControllerViewModel,
                private readonly services: IServiceFactory,
                getLowFareReader: () => IRoundTripLowFareReader) {


        this._departureDateSelectionStrategy = new DepartureDateSelectionStrategy(this.services, flightSearchController, this._getCurrentStrategy, this._setCurrentStrategy, getLowFareReader);
        this._returnDateSelectionStrategy = new ReturnDateSelectionStrategy(this.services, flightSearchController, this._getCurrentStrategy, this._setCurrentStrategy, getLowFareReader);
        this._currentStrategy = this._departureDateSelectionStrategy;


        const time = this.services.time;
        this._allMonths = time.getMonthsInRange(time.currentDate, time.addMonths(time.currentDate, 24));
        this._availableMonths = this._allMonths;

        makeObservable<this, '_currentStrategy' | '_availableMonths'>(this, {
            _currentStrategy: observable.ref,
            _availableMonths: observable.ref
        });

        this._reactions.push(reaction(() => this.flightSearchController.isOneWay,
            () => {
                if(this.flightSearchController.isOneWay) {
                    this._setCurrentStrategy(this._departureDateSelectionStrategy);
                } else {
                    if(this.flightSearchController.departureDate) {
                        this._setCurrentStrategy(this._returnDateSelectionStrategy);
                    } else {
                        this._setCurrentStrategy(this._departureDateSelectionStrategy);
                    }
                }
            }));

        this._reactions.push(reaction(() => this.flightSearchController.departureDate, (departureDate) => {
            if(!departureDate) {
                this._setCurrentStrategy(this._departureDateSelectionStrategy);
            }
        }))
    }

    private _reactions: IReactionDisposer[] = [];

    private readonly _departureDateSelectionStrategy: DepartureDateSelectionStrategy;
    private readonly _returnDateSelectionStrategy: ReturnDateSelectionStrategy;


    private _currentStrategy: IDateSelectionStrategy;

    private _getCurrentStrategy = () => {
        return this._currentStrategy;
    }

    private _setCurrentStrategy = (strategy: IDateSelectionStrategy) => {
        runInAction(() => {
            this._currentStrategy = strategy;
        })
    }

    get strategies(): IDateSelectionStrategy[] {
        if(this.flightSearchController.isOneWay) {
            return [this._departureDateSelectionStrategy]
        }


        return [this._departureDateSelectionStrategy, this._returnDateSelectionStrategy];
    }

    get isOnDepartureDateStrategy(): boolean {
        return this._currentStrategy instanceof DepartureDateSelectionStrategy;
    }

    isSameDateForDepartureAndReturn(date: Date): boolean {
        const departureDate = this.flightSearchController.departureDate;
        const returnDate = this.flightSearchController.returnDate;
        const time = this.services.time;


        return Boolean(departureDate && returnDate && time.areDatesEqual(date, departureDate) && time.areDatesEqual(date, returnDate));
    }

    get departureDate(): NullableDate {
        return this.flightSearchController.departureDate;
    }

    get returnDate(): NullableDate {
        return this.flightSearchController.returnDate;
    }

    getLowFare(date: Date): ILowFareResult {
        return this._currentStrategy.getLowFare(date);
    }

    getDepartureLowFare(date: Date): ILowFareResult {
        return this._departureDateSelectionStrategy.getLowFare(date);
    }

    getReturnLowFare(date: Date): ILowFareResult {
        return this._returnDateSelectionStrategy.getLowFare(date);
    }

    getDateAvailability(date: Date): IFlightDateScheduleAvailability {
        const lowFareStatus = this._currentStrategy.getLowFare(date).status;
        const dateAvailability = this._currentStrategy.getDateAvailability(date);

        if(dateAvailability.status === FlightScheduleAvailabilityStatusEnum.Available
            && lowFareStatus === LowFareStatusEnum.NoFare ) {
            return {
                status: FlightScheduleAvailabilityStatusEnum.SoldOut
            }
        }


        return dateAvailability;
    }

    isDateSelected(date: Date): boolean {
        return this.services.time.areDatesEqual(date, this.flightSearchController.departureDate)
               || this.services.time.areDatesEqual(date, this.flightSearchController.returnDate);
    }

    isDepartureDate(date: Date): boolean {
        return this.services.time.areDatesEqual(date, this.flightSearchController.departureDate);
    }

    isDateInsideSelection(date: Date): boolean {
        const searchController = this.flightSearchController;
        if(searchController.departureDate && searchController.returnDate) {
            return searchController.departureDate?.getTime() <= date.getTime()
                && date.getTime() <= searchController.returnDate?.getTime();
        } else {
            return false;
        }
    }

    clearDepartureDate(): void {
        this.flightSearchController.returnDate = null;
        this.flightSearchController.departureDate = null;
        this._setCurrentStrategy(this._departureDateSelectionStrategy);
    }

    clearReturnDate(): void {
        this.flightSearchController.returnDate = null;
        if(this.flightSearchController.departureDate) {
            this._setCurrentStrategy(this._returnDateSelectionStrategy);
        } else {
            this._setCurrentStrategy(this._departureDateSelectionStrategy);
        }

    }


    selectDate(date: Date): void {
        this._currentStrategy.setCurrentDate(date);
        if(this.flightSearchController.isOneWay) {
            return;
        }

        if(this._currentStrategy instanceof DepartureDateSelectionStrategy) {
            this._setCurrentStrategy(this._returnDateSelectionStrategy);
        } else {
            this._setCurrentStrategy(this._departureDateSelectionStrategy);
        }
    }

    private readonly _allMonths: MonthModel[] = [];
    private _availableMonths: MonthModel[] = [];
    get availableMonths(): MonthModel[] {
        return this._availableMonths;

    }

    private async _getIndexOfTheFirstMonthWithSchedule(): Promise<number> {

        for(let monthIndex = 0; monthIndex < this._allMonths.length; monthIndex++) {
            const month = this._allMonths[monthIndex];
            for(let d = month.firstDate; d <= month.lastDate; d = this.services.time.addDays(d, 1)) {
                const departureDateAvailability = await this._departureDateSelectionStrategy.getDateAvailabilityAsync(d);
                if(departureDateAvailability.status === FlightScheduleAvailabilityStatusEnum.Available) {
                    return monthIndex;
                }
            }
        }

        return -1;
    }
    async updateAvailableMonths(): Promise<void> {

        let firstMonthWithScheduleIndex = await this._getIndexOfTheFirstMonthWithSchedule();
        if(firstMonthWithScheduleIndex < 0) {
            return;
        }

        const result: MonthModel[] = this._allMonths.slice(firstMonthWithScheduleIndex);
        runInAction(() => {
            this._availableMonths = result;
        });

    }
    dispose(): void {
        this._reactions.forEach(disposeReaction => disposeReaction());
        this._reactions = [];
    }
}




